import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuthContext } from '@/providers/Auth';
import { firebaseAuthService } from '@/lib/firebase/auth';
import { trackingService } from '@/lib/tracking';
// Direct file imports to avoid pulling feature barrels (pages, etc.) and
// the circular dependency the campaign barrel would create via @/features/auth.
import { userApiService } from '@/features/user/services/userApi';
import { refUrlService } from '@/features/auth/services/refUrlService';
import { campaignApiService } from '@/features/campaign/services/campaignApi';
import { brokerOfferApiService } from '../services/brokerOfferApi';
import type {
  BrokerOfferApplicant,
  BrokerOtpConfirmRequest,
  BrokerOtpConfirmResponse,
  BrokerOtpRequestResponse,
} from '../types/brokerOffer';

export const BrokerOtpQueryKeys = {
  applicant: (offerId: string) => ['broker-offer-applicant', offerId] as const,
};

interface UseBrokerOtpReturn {
  applicant: BrokerOfferApplicant | undefined;
  isLoadingApplicant: boolean;
  requestOtp: () => Promise<BrokerOtpRequestResponse>;
  confirmOtp: (
    request: BrokerOtpConfirmRequest
  ) => Promise<BrokerOtpConfirmResponse>;
  isRequestingOtp: boolean;
  isConfirmingOtp: boolean;
}

export function useBrokerOtp(offerId: string | undefined): UseBrokerOtpReturn {
  const authContext = useAuthContext();
  if (!authContext) {
    throw new Error('useBrokerOtp must be used within an AuthProvider');
  }
  const { signIn } = authContext;

  const applicantQuery = useQuery({
    queryKey: BrokerOtpQueryKeys.applicant(offerId ?? ''),
    queryFn: () => brokerOfferApiService.getApplicant(offerId as string),
    enabled: !!offerId,
  });

  const requestOtpMutation = useMutation({
    mutationFn: () => brokerOfferApiService.requestOtp(offerId as string),
  });

  const confirmOtpMutation = useMutation({
    mutationFn: async (request: BrokerOtpConfirmRequest) => {
      // Carry the affiliate/CPS landing URL so the backend can attribute this
      // registration, mirroring the regular signUp flow.
      const refUrl = refUrlService.get();
      const response = await brokerOfferApiService.confirmOtp(
        offerId as string,
        refUrl ? { ...request, refUrl } : request
      );
      if (!response.isSuccess) {
        return response;
      }

      // Volt authorization handoff. The (proxied) confirm-otp returns a custom
      // token from Ocean; sign into Firebase with it...
      await firebaseAuthService.signInWithCustomToken(response.token);

      // ...then ensure a Volt account exists for this Firebase user (created
      // from identity data if missing). Provisioning only — no response body.
      await userApiService.ensureUser();
      signIn(response.user);

      trackingService.identify(response.user.id);
      trackingService.trackEvent('sign_in');
      if (response.isNew) {
        trackingService.trackEvent('sign_up');
      }

      if (refUrl) {
        // Clear before useCampaignTracking (global) reacts to the auth flip,
        // so the same URL is not reported twice. Fire-and-forget.
        refUrlService.clear();
        campaignApiService.track({ refUrl }).catch(() => {});
      }

      return response;
    },
  });

  return {
    applicant: applicantQuery.data,
    isLoadingApplicant: applicantQuery.isLoading,
    requestOtp: requestOtpMutation.mutateAsync,
    confirmOtp: confirmOtpMutation.mutateAsync,
    isRequestingOtp: requestOtpMutation.isPending,
    isConfirmingOtp: confirmOtpMutation.isPending,
  };
}
